import { Brain, Heart, Zap } from "lucide-react";

export function RoleTransferModel() {
    return (
        <div className="space-y-12 max-w-4xl mx-auto">

            <section className="space-y-6">
                <div className="space-y-2">
                    <h2 className="text-3xl font-bold tracking-tight text-primary">The Theoretical Framework</h2>
                    <p className="text-xl text-muted-foreground">
                        Synthesizing Daniel Pink's Motivation Theory with Spiegler's Agile Maturity Model.
                    </p>
                </div>

                <div className="p-6 bg-muted/30 border rounded-xl">
                    <h3 className="text-lg font-semibold mb-3">The Core Connection</h3>
                    <p className="text-muted-foreground leading-relaxed">
                        Daniel Pink provides the <strong>"Why"</strong> (Human Motivation), while Simone Spiegler provides the <strong>"How"</strong> (Role Distribution).
                        Together, they form a complete picture of high-performance teams: autonomy is not just a feeling, but a set of specific leadership tasks that must be mastered.
                    </p>
                </div>
            </section>

            {/* Daniel Pink Section */}
            <section className="space-y-6">
                <div className="flex items-center gap-3 border-b pb-4">
                    <Brain className="h-8 w-8 text-primary" />
                    <div>
                        <h3 className="text-2xl font-bold">1. Daniel Pink: The Foundation of Motivation</h3>
                        <p className="text-sm text-muted-foreground">Source: "Drive: The Surprising Truth About What Motivates Us"</p>
                    </div>
                </div>

                <p className="text-foreground/80">
                    To achieve <strong>FLOW</strong> (the state of optimal performance and engagement), knowledge workers need three intrinsic motivators.
                    However, Pink leaves open <em>how</em> exactly these are achieved in a team context.
                </p>

                <div className="grid md:grid-cols-3 gap-6">
                    <div className="p-4 border rounded-lg bg-card hover:bg-muted/5 transition-colors">
                        <div className="h-10 w-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4 text-blue-600 font-bold">A</div>
                        <h4 className="font-bold mb-2">Autonomy</h4>
                        <p className="text-sm text-muted-foreground">The desire to direct our own lives. In teams: self-organization and decision-making power.</p>
                    </div>
                    <div className="p-4 border rounded-lg bg-card hover:bg-muted/5 transition-colors">
                        <div className="h-10 w-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4 text-green-600 font-bold">M</div>
                        <h4 className="font-bold mb-2">Mastery</h4>
                        <p className="text-sm text-muted-foreground">The urge to get better and better at something that matters. In teams: continuous learning and skill growth.</p>
                    </div>
                    <div className="p-4 border rounded-lg bg-card hover:bg-muted/5 transition-colors">
                        <div className="h-10 w-10 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mb-4 text-purple-600 font-bold">P</div>
                        <h4 className="font-bold mb-2">Purpose</h4>
                        <p className="text-sm text-muted-foreground">The yearning to do what we do in the service of something larger than ourselves.</p>
                    </div>
                </div>
            </section>

            {/* Arrow Divider */}
            <div className="flex justify-center text-muted-foreground/30">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 animate-pulse"><path d="M12 5v14M19 12l-7 7-7-7" /></svg>
            </div>

            {/* Simone Spiegler Section */}
            <section className="space-y-6">
                <div className="flex items-center gap-3 border-b pb-4">
                    <Zap className="h-8 w-8 text-yellow-500" />
                    <div>
                        <h3 className="text-2xl font-bold">2. Simone Spiegler et al.: The Mechanism</h3>
                        <p className="text-sm text-muted-foreground">Source: "Leadership Gap in Agile Teams"</p>
                    </div>
                </div>

                <p className="text-foreground/80">
                    Spiegler answers Pink's open question: <strong>"How is Autonomy actually achieved?"</strong>
                    <br />
                    It is not magic. It is the result of the team mastering 9 specific leadership roles that define <strong>Self-Organization</strong>.
                </p>

                <div className="grid gap-4">
                    <div className="bg-muted p-4 rounded-lg border-l-4 border-blue-500">
                        <h4 className="font-bold text-sm uppercase tracking-wide text-blue-500 mb-3">Pink's Autonomy → Spiegler's 9 Roles</h4>
                        <p className="text-sm mb-4 font-medium">To achieve true Autonomy, the team must take ownership of these functions:</p>

                        <div className="grid md:grid-cols-2 gap-y-4 gap-x-8">
                            <div>
                                <h5 className="text-xs font-bold text-muted-foreground mb-2 uppercase tracking-widest">Interpersonal Autonomy</h5>
                                <ul className="space-y-1.5 text-sm text-foreground/90">
                                    <li className="flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-blue-500" />Coach</li>
                                    <li className="flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-blue-500" />Protector</li>
                                    <li className="flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-blue-500" />Moderator</li>
                                    <li className="flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-blue-500" />Disciplinizer</li>
                                    <li className="flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-blue-500" />Networker</li>
                                </ul>
                            </div>
                            <div>
                                <h5 className="text-xs font-bold text-muted-foreground mb-2 uppercase tracking-widest">Process Autonomy</h5>
                                <ul className="space-y-1.5 text-sm text-foreground/90">
                                    <li className="flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-blue-500" />Knowledge Enabler</li>
                                    <li className="flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-blue-500" />Method Champion</li>
                                    <li className="flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-blue-500" />Change Agent</li>
                                    <li className="flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-blue-500" />Helicopter</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-muted p-4 rounded-lg border-l-4 border-green-500">
                            <h4 className="font-bold text-sm uppercase tracking-wide text-green-500 mb-1">Pink's Mastery</h4>
                            <p className="text-sm text-muted-foreground">
                                This is the domain of the <strong>Makers & Specialists</strong>. The developers and designers bring the technical excellence and craft to the table.
                            </p>
                        </div>

                        <div className="bg-muted p-4 rounded-lg border-l-4 border-purple-500">
                            <h4 className="font-bold text-sm uppercase tracking-wide text-purple-500 mb-1">Pink's Purpose</h4>
                            <p className="text-sm text-muted-foreground">
                                This is the domain of the <strong>Product Owner</strong>. They provide the 'Why', the vision, and the direction that the team works towards.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-primary text-primary-foreground p-8 rounded-2xl text-center space-y-4">
                <Heart className="h-10 w-10 mx-auto opacity-90" />
                <h3 className="text-2xl font-bold">The Synthesis</h3>
                <div className="text-lg opacity-90 max-w-2xl mx-auto space-y-2">
                    <p>"High performance requires a clear separation of concerns:"</p>
                    <ul className="text-sm md:text-base font-medium space-y-1">
                        <li>The PO provides Purpose.</li>
                        <li>The Makers ensure Mastery.</li>
                        <li>The Team ensures <strong>Autonomy</strong> via the 9 Roles.</li>
                    </ul>
                </div>
            </section>

        </div>
    );
}
